import {
  Alert,
  Box,
  Breadcrumbs,
  Divider,
  FormControlLabel,
  LinearProgress,
  Link,
  Paper,
  Stack,
  Switch,
  TablePagination,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AuditFilters from "../components/audit/AuditFilters";
import AuditTable from "../components/audit/AuditTable";
import {
  fetchAuditLogs,
  selectAuditError,
  selectAuditFilters,
  selectAuditLoading,
  selectAuditMeta,
  selectAuditPagination,
  setAuditPage,
  setAuditPageSize,
} from "../store/slices/auditTrailSlice";

const AuditTrailPage = () => {
  const dispatch = useDispatch();
  const filters = useSelector(selectAuditFilters);
  const pagination = useSelector(selectAuditPagination);
  const meta = useSelector(selectAuditMeta);
  const loading = useSelector(selectAuditLoading);
  const error = useSelector(selectAuditError);
  const [dense, setDense] = useState(false);

  useEffect(() => {
    dispatch(fetchAuditLogs());
  }, [
    dispatch,
    filters.search,
    filters.startDate,
    filters.endDate,
    pagination.page,
    pagination.pageSize,
  ]);

  useEffect(() => {
    if (!meta?.totalPages) return;
    const maxPage = Math.max(0, meta.totalPages - 1);
    if (pagination.page > maxPage) {
      dispatch(setAuditPage(maxPage));
    }
  }, [dispatch, meta, pagination.page]);

  const totalItems = useMemo(
    () => meta?.totalItems ?? 0,
    [meta?.totalItems]
  );

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
        }}
      >
        <Box>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator=">"
            sx={{ fontSize: 12, mb: 1, color: "text.secondary" }}
          >
            <Link underline="hover" color="inherit" href="#">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="#">
              Security
            </Link>
            <Typography color="text.primary">Audit Trail</Typography>
          </Breadcrumbs>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Audit Trail
          </Typography>
          <Typography color="text.secondary">
            Review authentication activities performed by admins.
          </Typography>
        </Box>
      </Box>

      <Paper
        sx={{
          p: { xs: 2, sm: 3 },
          boxShadow: "0px 24px 48px rgba(15, 23, 42, 0.05)",
        }}
      >
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <AuditFilters />

        <Box
          sx={{
            width: "100%",
            overflowX: "auto",
            border: "1px solid rgba(148, 163, 184, 0.16)",
            borderRadius: 1,
            mt: 3,
            backgroundColor: "background.paper",
          }}
        >
          <AuditTable dense={dense} />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <FormControlLabel
            control={
              <Switch
                checked={dense}
                onChange={(event) => setDense(event.target.checked)}
              />
            }
            label="Dense"
          />
          <TablePagination
            component="div"
            count={totalItems}
            rowsPerPage={pagination.pageSize}
            rowsPerPageOptions={[10, 25, 50]}
            page={pagination.page}
            onPageChange={(_, newPage) => dispatch(setAuditPage(newPage))}
            onRowsPerPageChange={(event) => {
              const value = parseInt(event.target.value, 10);
              dispatch(setAuditPageSize(value));
            }}
            sx={{
              "& .MuiTablePagination-toolbar": {
                px: { xs: 0, sm: 2 },
              },
            }}
          />
        </Stack>
      </Paper>
    </Stack>
  );
};

export default AuditTrailPage;
